import { useState, useEffect, useCallback } from "react";
import { errorColor, successColor, useAuth } from "../../sdk";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

export function useResavenuePricing() {
  const { authFetch } = useAuth();
  const { hotelId } = useParams();
  const [ratePlans, setRatePlans] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [changedCells, setChangedCells] = useState(new Set());
  const [snackbarMsg, setSnackBarMsg] = useState({
    open: false,
    color: "",
    msg: ""
  });

  const fetchRatePlans = useCallback(async () => {
    try {
      setIsFetching(true);
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/get-room-rate-plan`,
      });

      const { data, response } = await get();
      if (response.ok && data) {
        setRatePlans([...data].sort((a, b) => b.isBaseRoom - a.isBaseRoom));
      }
    } catch (err) {
      console.error("Error fetching rate plans", err);
    } finally {
      setIsFetching(false);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    fetchRatePlans();
  }, [fetchRatePlans]);

  const changeHandler = (index, field, value) => {
    setRatePlans((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setChangedCells((prev) => {
      const newSet = new Set(prev);
      newSet.add(`${index}-${field}`);
      return newSet;
    });
  };

  const handleSaveRatePlans = async () => {
    setIsLoading(true);
    try {
      const payload = ratePlans.map((plan) => ({
        id: plan?.RoomRatePlanId,
        addToBasePriceLow: plan?.addToBasePriceLow
          ? Number(plan?.addToBasePriceLow)
          : null,
        addToBasePriceHigh: plan?.addToBasePriceHigh
          ? Number(plan?.addToBasePriceHigh)
          : null,
        minThresholdWeekday: plan?.minThresholdWeekday
          ? Number(plan?.minThresholdWeekday)
          : null,
        maxThresholdWeekday: plan?.maxThresholdWeekday
          ? Number(plan?.maxThresholdWeekday)
          : null,
        minThresholdWeekend: plan?.minThresholdWeekend
          ? Number(plan?.minThresholdWeekend)
          : null,
        maxThresholdWeekend: plan?.maxThresholdWeekend
          ? Number(plan?.maxThresholdWeekend)
          : null,
        minThresholdWeekdayLow: plan?.minThresholdWeekdayLow
          ? Number(plan?.minThresholdWeekdayLow)
          : null,
        maxThresholdWeekdayLow: plan?.maxThresholdWeekdayLow
          ? Number(plan?.maxThresholdWeekdayLow)
          : null,
        minThresholdWeekendLow: plan?.minThresholdWeekendLow
          ? Number(plan?.minThresholdWeekendLow)
          : null,
        maxThresholdWeekendLow: plan?.maxThresholdWeekendLow
          ? Number(plan?.maxThresholdWeekendLow)
          : null,
      }));

      const { put } = await authFetch({
        path: `/hotel/${hotelId}/room-rate-plan/pricing-details`,
      });

      const { response } = await put(payload);
      if (response.ok) {
        setSnackBarMsg({
          open: true,
          color: successColor,
          msg: response?.data?.type || "Rate Plans Updated Successfully!",
        });
        fetchRatePlans();
        setChangedCells(new Set());
      }else{
        setSnackBarMsg({
          open: true,
          color: errorColor,
          msg: response?.data?.messageToUser || "Error Updating Rate Plans",
        })
      }
    } catch (err) {
      setSnackBarMsg({
          open: true,
          color: errorColor,
          msg: "Something Went Wrong",
        })
      console.error("Error saving rate plans ❌", err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    changedCells,
    ratePlans,
    changeHandler,
    handleSaveRatePlans,
    isFetching,
    isLoading,
    fetchRatePlans,
    snackbarMsg,
    setSnackBarMsg
  };
}
